/**
 * Theme Utility Functions
 *
 * Helper functions for theme management, CSS variable application,
 * and color manipulation.
 */

/**
 * Apply theme colors to DOM as CSS custom properties
 * @param {Object} theme - Theme configuration object
 */
export function applyThemeToDOM(theme) {
  if (!theme || !theme.colors) return;

  const root = document.documentElement;
  const { colors } = theme;

  // Background colors
  root.style.setProperty('--color-bg-primary', colors.background.primary);
  root.style.setProperty('--color-bg-secondary', colors.background.secondary);
  root.style.setProperty('--color-bg-tertiary', colors.background.tertiary);
  root.style.setProperty('--color-bg-elevated', colors.background.elevated);
  root.style.setProperty('--color-bg-hover', colors.background.hover);

  // Text colors
  root.style.setProperty('--color-text-primary', colors.text.primary);
  root.style.setProperty('--color-text-secondary', colors.text.secondary);
  root.style.setProperty('--color-text-muted', colors.text.muted);
  root.style.setProperty('--color-text-disabled', colors.text.disabled);
  root.style.setProperty('--color-text-inverse', colors.text.inverse);
  root.style.setProperty('--color-text-on-accent', colors.text.onAccent);

  // Border colors
  root.style.setProperty('--color-border-default', colors.border.default);
  root.style.setProperty('--color-border-subtle', colors.border.subtle);
  root.style.setProperty('--color-border-strong', colors.border.strong);
  root.style.setProperty('--color-border-accent', colors.border.accent);

  // Accent colors
  root.style.setProperty('--color-accent-primary', colors.accent.primary);
  root.style.setProperty('--color-accent-secondary', colors.accent.secondary);
  root.style.setProperty('--color-accent-light', colors.accent.light);
  root.style.setProperty('--color-accent-hover', colors.accent.hover);
  root.style.setProperty('--color-accent-disabled', colors.accent.disabled);

  // Semantic colors
  root.style.setProperty('--color-success', colors.success);
  root.style.setProperty('--color-warning', colors.warning);
  root.style.setProperty('--color-error', colors.error);
  root.style.setProperty('--color-info', colors.info);

  // Interactive states
  root.style.setProperty('--color-hover', colors.hover);
  root.style.setProperty('--color-focus', colors.focus);
  root.style.setProperty('--color-active', colors.active);
  root.style.setProperty('--color-disabled', colors.disabled);

  // Gradients
  root.style.setProperty('--color-gradient-from', colors.gradient.from);
  root.style.setProperty('--color-gradient-via', colors.gradient.via);
  root.style.setProperty('--color-gradient-to', colors.gradient.to);
  root.style.setProperty('--color-gradient-accent-from', colors.gradient.accent.from);
  root.style.setProperty('--color-gradient-accent-to', colors.gradient.accent.to);

  // Special purpose
  root.style.setProperty('--color-overlay', colors.overlay);
  root.style.setProperty('--color-shadow', colors.shadow);

  // Add theme type class to document
  document.documentElement.classList.remove('theme-dark', 'theme-light', 'theme-custom');
  document.documentElement.classList.add(`theme-${theme.type}`);
}

/**
 * Detect system color scheme preference
 * @returns {string} 'dark' or 'light'
 */
export function detectSystemPreference() {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

/**
 * Listen for system theme changes
 * @param {Function} callback - Called when system preference changes
 * @returns {Function} Cleanup function to remove listener
 */
export function watchSystemPreference(callback) {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handler = (e) => {
    callback(e.matches ? 'dark' : 'light');
  };

  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }
  // Fallback for older browsers
  else if (mediaQuery.addListener) {
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }

  return () => {};
}

/**
 * Validate theme configuration
 * @param {Object} theme - Theme to validate
 * @returns {boolean} True if valid
 */
export function validateTheme(theme) {
  if (!theme || typeof theme !== 'object') return false;
  if (!theme.id || !theme.name || !theme.type) return false;
  if (!theme.colors || typeof theme.colors !== 'object') return false;

  const required = [
    'background', 'text', 'border', 'accent',
    'success', 'warning', 'error', 'info'
  ];

  return required.every(key => key in theme.colors);
}

/**
 * Deep clone theme object
 * @param {Object} theme - Theme to clone
 * @returns {Object} Cloned theme
 */
export function cloneTheme(theme) {
  return JSON.parse(JSON.stringify(theme));
}

/**
 * Merge theme configurations
 * @param {Object} base - Base theme
 * @param {Object} overrides - Theme overrides
 * @returns {Object} Merged theme
 */
export function mergeThemes(base, overrides) {
  const merged = cloneTheme(base);

  if (overrides.name) merged.name = overrides.name;
  if (overrides.colors) {
    merged.colors = {
      ...merged.colors,
      ...overrides.colors,
      background: { ...merged.colors.background, ...overrides.colors.background },
      text: { ...merged.colors.text, ...overrides.colors.text },
      border: { ...merged.colors.border, ...overrides.colors.border },
      accent: { ...merged.colors.accent, ...overrides.colors.accent },
      gradient: {
        ...merged.colors.gradient,
        ...overrides.colors.gradient,
        accent: { ...merged.colors.gradient.accent, ...overrides.colors.gradient?.accent }
      }
    };
  }

  return merged;
}

/**
 * Export theme as JSON file
 * @param {Object} theme - Theme to export
 * @param {string} filename - Optional filename
 */
export function exportThemeAsJSON(theme, filename = null) {
  const json = JSON.stringify(theme, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `${theme.name.toLowerCase().replace(/\s+/g, '-')}-theme.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Import theme from JSON file
 * @param {File} file - File to import
 * @returns {Promise<Object>} Parsed theme object
 */
export function importThemeFromJSON(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const theme = JSON.parse(e.target.result);
        if (validateTheme(theme)) {
          resolve(theme);
        } else {
          reject(new Error('Invalid theme format'));
        }
      } catch (error) {
        reject(new Error('Failed to parse theme file'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color code
 * @returns {Object} RGB object {r, g, b}
 */
export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Convert RGB to hex color
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} Hex color code
 */
export function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Calculate relative luminance of a color
 * @param {string} hex - Hex color code
 * @returns {number} Relative luminance (0-1)
 */
export function getLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(val => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculate contrast ratio between two colors
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @returns {number} Contrast ratio (1-21)
 */
export function getContrastRatio(color1, color2) {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Check if color combination meets WCAG AA standards
 * @param {string} foreground - Foreground color
 * @param {string} background - Background color
 * @returns {boolean} True if meets WCAG AA
 */
export function meetsWCAG_AA(foreground, background) {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 4.5; // WCAG AA standard for normal text
}
